import React, { useState, useEffect } from "react";
import {
  BarChart3,
  Users,
  DollarSign,
  Calendar,
  Shield,
  TrendingUp,
  Clock,
  FileText,
  CheckCircle,
  Menu,
  X,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useHistory } from "react-router-dom";

export default function ContractorLanding() {
  const history = useHistory();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("contractor");
  const [animateCharts, setAnimateCharts] = useState(false);

  const handleLoginClick = () => {
    history.push("/login");
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document
      .querySelectorAll(".fade-target")
      .forEach((el) => observer.observe(el));

    // Trigger chart animations
    setTimeout(() => setAnimateCharts(true), 500);

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Employee Management",
      description:
        "Track daily attendance, work hours, and assign employees to specific job sites effortlessly",
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Salary & Payroll",
      description:
        "Automated salary calculations based on work days and hours with detailed payment records",
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Profit Analytics",
      description:
        "Real-time profit tracking with comprehensive financial reports and expense management",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Role-Based Access",
      description:
        "Hierarchical system: Contractor → Managers → Users with customizable permissions",
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Job Site Tracking",
      description:
        "Create and manage multiple workplaces, assign teams, and monitor progress daily",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Expense Control",
      description:
        "Track all project expenses with approval workflows and budget monitoring",
    },
  ];

  const demoData = {
    employees: [
      { name: "John Smith", days: 22, salary: "$3,200", status: "Active" },
      { name: "Mike Johnson", days: 20, salary: "$2,800", status: "Active" },
      { name: "Sarah Wilson", days: 18, salary: "$2,600", status: "Active" },
    ],
    sites: [
      {
        name: "Downtown Tower",
        employees: 12,
        revenue: "$45,000",
        profit: "$12,000",
      },
      {
        name: "Harbor Bridge",
        employees: 8,
        revenue: "$32,000",
        profit: "$8,500",
      },
      {
        name: "Riverside Mall",
        employees: 15,
        revenue: "$58,000",
        profit: "$15,200",
      },
    ],
  };

  // Chart data
  const monthlyRevenue = [
    { month: "Jan", revenue: 45, expenses: 32 },
    { month: "Feb", revenue: 52, expenses: 35 },
    { month: "Mar", revenue: 48, expenses: 33 },
    { month: "Apr", revenue: 61, expenses: 38 },
    { month: "May", revenue: 58, expenses: 36 },
    { month: "Jun", revenue: 70, expenses: 42 },
  ];

  const expenseBreakdown = [
    { category: "Salaries", amount: 45, color: "#3b82f6", percentage: 45 },
    { category: "Materials", amount: 30, color: "#10b981", percentage: 30 },
    { category: "Equipment", amount: 15, color: "#f59e0b", percentage: 15 },
    { category: "Other", amount: 10, color: "#8b5cf6", percentage: 10 },
  ];

  // Donut chart component
  const DonutChart = ({ data, size = 200 }) => {
    const radius = size / 2 - 20;
    const circumference = 2 * Math.PI * radius;
    let currentAngle = -90;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {data.map((item, index) => {
            const dashArray = (item.percentage / 100) * circumference;
            const dashOffset = circumference - dashArray;
            const rotation = currentAngle;
            currentAngle += (item.percentage / 100) * 360;

            return (
              <circle
                key={index}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth="30"
                strokeDasharray={`${
                  animateCharts ? dashArray : 0
                } ${circumference}`}
                strokeDashoffset={0}
                transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
                className="transition-all duration-1000 ease-out"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">100%</div>
            <div className="text-xs text-gray-600">Total</div>
          </div>
        </div>
      </div>
    );
  };

  // Bar chart component
  const BarChart = ({ data }) => {
    const maxValue = Math.max(
      ...data.map((d) => Math.max(d.revenue, d.expenses))
    );

    return (
      <div className="space-y-6">
        {data.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700">{item.month}</span>
              <span className="text-gray-600">
                ${item.revenue}K / ${item.expenses}K
              </span>
            </div>
            <div className="flex gap-2 h-8">
              <div className="flex-1 bg-gray-200 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-1000 ease-out"
                  style={{
                    width: animateCharts
                      ? `${(item.revenue / maxValue) * 100}%`
                      : "0%",
                  }}
                />
              </div>
              <div className="flex-1 bg-gray-200 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-1000 ease-out"
                  style={{
                    width: animateCharts
                      ? `${(item.expenses / maxValue) * 100}%`
                      : "0%",
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        <div className="flex justify-center gap-6 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded"></div>
            <span className="text-sm text-gray-600">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-red-600 rounded"></div>
            <span className="text-sm text-gray-600">Expenses</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
        .fade-target {
          opacity: 0;
        }
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-down {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>

      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">
                NeholTorem
              </span>
            </div>

            <div className="hidden md:flex space-x-8">
              <a
                href="#features"
                className="text-gray-700 hover:text-blue-600 transition"
              >
                Features
              </a>
              <a
                href="#demo"
                className="text-gray-700 hover:text-blue-600 transition"
              >
                Demo
              </a>
              <a
                href="#pricing"
                className="text-gray-700 hover:text-blue-600 transition"
              >
                Pricing
              </a>
              <a
                href="#contact"
                className="text-gray-700 hover:text-blue-600 transition"
              >
                Contact
              </a>
            </div>

            <div className="hidden md:flex space-x-4">
              <button
                onClick={handleLoginClick}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
              >
                Sign In
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md">
                Start Free Trial
              </button>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t animate-slide-down">
            <div className="px-4 py-4 space-y-3">
              <a
                href="#features"
                className="block text-gray-700 hover:text-blue-600"
              >
                Features
              </a>
              <a
                href="#demo"
                className="block text-gray-700 hover:text-blue-600"
              >
                Demo
              </a>
              <a
                href="#pricing"
                className="block text-gray-700 hover:text-blue-600"
              >
                Pricing
              </a>
              <a
                href="#contact"
                className="block text-gray-700 hover:text-blue-600"
              >
                Contact
              </a>
              <button
                onClick={handleLoginClick}
                className="w-full px-4 py-2 text-blue-600 border border-blue-600 rounded-lg"
              >
                Sign In
              </button>
              <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg">
                Start Free Trial
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 fade-target">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              📱 Available on Mobile & Web
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Manage Your Construction Business Like a{" "}
              <span className="text-blue-600">Pro</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Complete workforce management solution for contractors. Track
              employees, manage salaries, monitor profits, and control expenses
              all in one powerful platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-lg text-lg font-semibold">
                Get Started Free
              </button>
              <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-blue-600 hover:text-blue-600 transition text-lg font-semibold">
                Watch Demo
              </button>
            </div>
            <div className="mt-8 flex items-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>14-day free trial</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Today's Overview
                </h3>
                <span className="text-sm text-green-600 font-semibold">
                  Live
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <Users className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-gray-900">142</p>
                  <p className="text-sm text-gray-600">Active Employees</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <DollarSign className="w-8 h-8 text-green-600 mb-2" />
                  <p className="text-2xl font-bold text-gray-900">$52K</p>
                  <p className="text-sm text-gray-600">Monthly Profit</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <Calendar className="w-8 h-8 text-purple-600 mb-2" />
                  <p className="text-2xl font-bold text-gray-900">8</p>
                  <p className="text-sm text-gray-600">Active Sites</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <TrendingUp className="w-8 h-8 text-orange-600 mb-2" />
                  <p className="text-2xl font-bold text-gray-900">+24%</p>
                  <p className="text-sm text-gray-600">Growth Rate</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Revenue vs Expenses</span>
                  <span className="text-green-600 font-semibold">+$15,200</span>
                </div>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full"
                    style={{ width: "68%" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 fade-target"
      >
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-xl text-gray-600">
            Powerful features designed for modern contractors
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition border border-gray-100 hover:border-blue-200"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works - Hierarchy */}
      <section className="bg-white py-20 fade-target">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Hierarchical Access Control
            </h2>
            <p className="text-xl text-gray-600">
              Complete control over who sees what
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row justify-center items-start md:items-center space-y-8 md:space-y-0 md:space-x-8">
              {/* Contractor */}
              <div className="flex-1 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Shield className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Contractor
                </h3>
                <p className="text-gray-600 mb-4">Full access to everything</p>
                <ul className="text-sm text-left bg-blue-50 rounded-lg p-4 space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Create workplaces</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Manage all employees</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Create manager accounts</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Set permissions & access</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>View all financial data</span>
                  </li>
                </ul>
              </div>

              {/* Arrow */}
              <div className="hidden md:block text-blue-600">
                <div className="text-4xl">→</div>
              </div>

              {/* Manager */}
              <div className="flex-1 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Manager
                </h3>
                <p className="text-gray-600 mb-4">Assigned site access</p>
                <ul className="text-sm text-left bg-green-50 rounded-lg p-4 space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Access assigned sites</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Enter daily attendance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Create user accounts</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Submit expenses (if allowed)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>View permitted data</span>
                  </li>
                </ul>
              </div>

              {/* Arrow */}
              <div className="hidden md:block text-green-600">
                <div className="text-4xl">→</div>
              </div>

              {/* User */}
              <div className="flex-1 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Clock className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">User</h3>
                <p className="text-gray-600 mb-4">Limited access</p>
                <ul className="text-sm text-left bg-purple-50 rounded-lg p-4 space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>View assigned tasks</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Check-in/out of sites</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>View own work history</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Access basic reports</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Submit timesheets</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section with Tables */}
      <section
        id="demo"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 fade-target"
      >
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            See It In Action
          </h2>
          <p className="text-xl text-gray-600">
            Real-time data visualization and management
          </p>
        </div>

        <div className="space-y-8">
          {/* Employee Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <Users className="w-6 h-6 mr-2" />
                Employee Management
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Employee Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Days Worked
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Salary
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {demoData.employees.map((emp, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-gray-900">{emp.name}</td>
                      <td className="px-6 py-4 text-gray-700">{emp.days}</td>
                      <td className="px-6 py-4 text-gray-900 font-semibold">
                        {emp.salary}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          {emp.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Job Sites Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <Calendar className="w-6 h-6 mr-2" />
                Job Site Performance
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Site Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Employees
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Revenue
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Profit
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {demoData.sites.map((site, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        {site.name}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {site.employees}
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-semibold">
                        {site.revenue}
                      </td>
                      <td className="px-6 py-4 text-green-600 font-bold">
                        {site.profit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics & Charts Section */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-20 fade-target">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Powerful Analytics Dashboard
            </h2>
            <p className="text-xl text-gray-600">
              Track your business performance with beautiful visualizations
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex items-center text-green-600 text-sm font-semibold">
                  <ArrowUp className="w-4 h-4" />
                  <span>12.5%</span>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">$135K</h3>
              <p className="text-gray-600 text-sm">Total Revenue</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex items-center text-green-600 text-sm font-semibold">
                  <ArrowUp className="w-4 h-4" />
                  <span>8.2%</span>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">$52K</h3>
              <p className="text-gray-600 text-sm">Net Profit</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex items-center text-red-600 text-sm font-semibold">
                  <ArrowDown className="w-4 h-4" />
                  <span>2.1%</span>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">142</h3>
              <p className="text-gray-600 text-sm">Active Employees</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex items-center text-green-600 text-sm font-semibold">
                  <ArrowUp className="w-4 h-4" />
                  <span>15.3%</span>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">8</h3>
              <p className="text-gray-600 text-sm">Active Projects</p>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Bar Chart */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Revenue vs Expenses
                </h3>
                <span className="text-sm text-gray-600">Last 6 Months</span>
              </div>
              <BarChart data={monthlyRevenue} />
            </div>

            {/* Donut Chart */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Expense Breakdown
                </h3>
                <span className="text-sm text-gray-600">This Month</span>
              </div>
              <div className="flex flex-col items-center">
                <DonutChart data={expenseBreakdown} size={220} />
                <div className="mt-6 grid grid-cols-2 gap-4 w-full">
                  {expenseBreakdown.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {item.category}
                        </p>
                        <p className="text-xs text-gray-600">
                          {item.percentage}% (${item.amount}K)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Progress Chart */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Project Completion
              </h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Downtown Tower
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      85%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-1000"
                      style={{ width: animateCharts ? "85%" : "0%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Harbor Bridge
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      62%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-1000"
                      style={{ width: animateCharts ? "62%" : "0%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Riverside Mall
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      45%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-1000"
                      style={{ width: animateCharts ? "45%" : "0%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      City Plaza
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      28%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-1000"
                      style={{ width: animateCharts ? "28%" : "0%" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Performance Metrics
              </h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      Efficiency Rate
                    </p>
                    <p className="text-2xl font-bold text-blue-600">94.2%</p>
                  </div>
                  <div className="w-16 h-16 rounded-full border-4 border-blue-600 flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      On-Time Delivery
                    </p>
                    <p className="text-2xl font-bold text-green-600">89.5%</p>
                  </div>
                  <div className="w-16 h-16 rounded-full border-4 border-green-600 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      Client Satisfaction
                    </p>
                    <p className="text-2xl font-bold text-purple-600">
                      4.8/5.0
                    </p>
                  </div>
                  <div className="w-16 h-16 rounded-full border-4 border-purple-600 flex items-center justify-center">
                    <Users className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-20 fade-target">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of contractors who've streamlined their operations
            with ContractorPro
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition shadow-lg text-lg font-semibold">
              Start Free Trial
            </button>
            <button className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600 transition text-lg font-semibold">
              Schedule Demo
            </button>
          </div>
          <p className="text-blue-100 mt-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                <span className="text-xl font-bold text-white">NeholTorem</span>
              </div>
              <p className="text-sm">
                The complete solution for modern contractors to manage their
                workforce and finances.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Demo
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-400 transition">
                    Security
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>© 2024 NeholTorem. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
